'use client'

import Link, { type LinkProps } from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { PropsWithChildren, MouseEvent } from 'react'

import { cn, isMatch } from '@/shared/lib'
import { NewBadge } from '@/shared/ui'

export const List = ({ children }: PropsWithChildren) => {
  return (
    <nav>
      <ul className="flex items-center gap-6">{children}</ul>
    </nav>
  )
}

type ItemProps = LinkProps & {
  accessible?: boolean
  newBadge?: boolean
  onNotAccessible?: (pathname: string) => void
  excludedPaths: string[]
  className?: string
}

export const Item = ({
  href,
  newBadge = false,
  className,
  children,
  accessible = true,
  onNotAccessible,
  excludedPaths,
  ...restProps
}: PropsWithChildren<ItemProps>) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const t = useTranslations('navigation')

  const hrefString = typeof href === 'string' ? href : href.pathname || ''
  const selected = (() => {
    let isSelected = pathname ? pathname.startsWith(hrefString) : false

    if (!isSelected) {
      return isSelected
    }

    if (pathname && excludedPaths.length) {
      isSelected = !isMatch(pathname || '', excludedPaths)
    }

    return isSelected
  })()

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!accessible) {
      event.preventDefault()
      onNotAccessible?.(hrefString)

      return
    }

    const isEmptySearchParams = !searchParams?.toString()

    if (pathname === hrefString && isEmptySearchParams) {
      event.preventDefault()
    }
  }

  return (
    <li>
      <Link
        href={href}
        onClick={handleClick}
        className={cn(
          'typo-body1 flex h-10 w-[80px] items-center justify-center rounded-sm py-[7px] font-medium text-black transition-all',
          'hover:bg-purple-50',
          selected && 'font-bold',
        )}
        {...(selected && { 'aria-current': 'page' })}
        {...restProps}
      >
        <div
          className={cn(
            '-mb-0.5 box-border flex h-fit w-fit items-center gap-[3px] border-b-2 border-transparent',
            selected && 'border-black',
            className,
          )}
        >
          <span>{t(children as string)}</span>
          {newBadge && <NewBadge />}
        </div>
      </Link>
    </li>
  )
}
